/**
 * CompanionVideoPlayer.tsx
 * Reusable video player component with play button overlay for onboarding/welcome screens.
 */
import React, { useCallback, useRef, useState } from "react";
import { View, Text, Pressable, StyleSheet, Image } from "react-native";
import { MotiView } from "moti";
import { LinearGradient } from "expo-linear-gradient";
import { PlayIcon } from "@phosphor-icons/react";
import { colors, spacing, radii } from "../../../styles/design-system";
import onboardingVideo from "../../../assets/onboarding_video_captions.mp4";

type VideoElementWithFullscreen = HTMLVideoElement & {
  webkitEnterFullscreen?: () => Promise<void> | void;
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

const webVideoStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
  zIndex: 0,
};

export interface CompanionVideoPlayerProps {
  style?: any;
}

export function CompanionVideoPlayer({ style }: CompanionVideoPlayerProps) {
  const [isPlayHovered, setIsPlayHovered] = useState(false);
  const [hasVideoStarted, setHasVideoStarted] = useState(false);
  const videoRef = useRef<VideoElementWithFullscreen | null>(null);
  const isSafari = typeof navigator !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const safariVideoSrc = isSafari
    ? typeof onboardingVideo === "string"
      ? onboardingVideo
      : Image.resolveAssetSource(onboardingVideo)?.uri
    : undefined;

  const handleVideoPlay = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    const playPromise = videoRef.current.play();
    if (playPromise) {
      void playPromise.catch(() => null);
    }
  }, []);

  return (
    <View style={[styles.videoSection, style]}>
      {typeof window !== "undefined" ? (
        <View style={styles.videoContainer}>
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            src={safariVideoSrc}
            style={webVideoStyle}
            onPlay={() => setHasVideoStarted(true)}
            aria-label="Axio onboarding overview video">
            {!isSafari ? <source src={onboardingVideo} type="video/mp4" /> : null}
            Your browser does not support the video tag.
          </video>
          {!hasVideoStarted && (
            <Pressable
              accessibilityLabel="Play onboarding video"
              accessibilityRole="button"
              style={styles.videoOverlay}
              onPress={handleVideoPlay}
              onHoverIn={() => setIsPlayHovered(true)}
              onHoverOut={() => setIsPlayHovered(false)}>
              <LinearGradient
                colors={[colors.brand[600], colors.brand[800]]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.videoGradient}>
                <View style={styles.videoContent}>
                  <MotiView
                    animate={{
                      scale: isPlayHovered ? 1.1 : 1,
                    }}
                    transition={{
                      type: "timing",
                      duration: 150,
                    }}
                    style={styles.playButtonContainer}>
                    <PlayIcon size={28} color={colors.white} weight="fill" style={styles.playIcon} />
                  </MotiView>
                  <Text style={styles.videoText}>Watch intro (90 sec)</Text>
                </View>
              </LinearGradient>
            </Pressable>
          )}
        </View>
      ) : (
        <Pressable
          style={[styles.videoContainer, styles.videoPlaceholder]}
          onHoverIn={() => setIsPlayHovered(true)}
          onHoverOut={() => setIsPlayHovered(false)}>
          <LinearGradient
            colors={[colors.brand[600], colors.brand[800]]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.videoGradient}>
            <View style={styles.videoContent}>
              <MotiView
                animate={{
                  scale: isPlayHovered ? 1.1 : 1,
                }}
                transition={{
                  type: "timing",
                  duration: 150,
                }}
                style={styles.playButtonContainer}>
                <PlayIcon size={28} color={colors.white} weight="fill" style={styles.playIcon} />
              </MotiView>
              <Text style={styles.videoText}>Watch intro (90 sec)</Text>
            </View>
          </LinearGradient>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  videoSection: {
    marginBottom: spacing.lg,
  },
  videoContainer: {
    width: "100%",
    aspectRatio: 16 / 9,
    borderRadius: radii.lg,
    overflow: "hidden",
    position: "relative",
    backgroundColor: colors.grey[900],
  },
  videoPlaceholder: {
    backgroundColor: colors.grey[900],
  },
  videoOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  videoGradient: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  videoContent: {
    alignItems: "center",
    gap: spacing.md,
  },
  playButtonContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  playIcon: {
    marginLeft: 4,
  },
  videoText: {
    fontSize: 16,
    fontWeight: "600",
    color: colors.white,
    textAlign: "center",
  },
});
